import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, OnDestroy, ChangeDetectorRef, NgZone, signal, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AIScreeningService } from '../../services/ai-Screening';
import { AiInterviewSessionService } from '../../services/AiInterviewSessionService';
import { ScreeningRoundResponseData } from '../../models/responseModels/screeningRoundDetails';
import { ConnectivityService, ConnectivityQuality } from '../../services/connectivity.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-interview-session',
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './interview-session.html',
  styleUrl: './interview-session.css'

})
export class InterviewSession implements OnInit, OnDestroy {
  isB2cPractice: boolean = false;
  private destroy$ = new Subject<void>();
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;
  volume = 0;
  cameraStream: MediaStream | null = null;
  micStream: MediaStream | null = null;
  audioContext: AudioContext | null = null;
  analyser: AnalyserNode | null = null;
  dataArray!: Uint8Array;
  animationId: number | null = null;
  cameraActive = false;
  micActive = false;
  isRequestingPermissions = false;
  permissionsGranted = false;
  cameraPermissionDenied = false;
  micPermissionDenied = false;
  permissionErrorMessage = '';
  isPermissionsRevoked = false;
  cameraPermissionStatus: PermissionStatus | null = null;
  micPermissionStatus: PermissionStatus | null = null;
  networkQuality: ConnectivityQuality = 'CHECKING';
  hasAudioBeenDetected = false;
  lastAudioDetectedTime = 0;
  interviewForm !: FormGroup;
  roundDetails = signal<ScreeningRoundResponseData | null>(null);
  isLoading: Boolean = false;
  candidateId?: number | string;
  sessionId?: number | string;
  screeningId?: string;
  screeningAccessCode: string | null = "";

  audioDevices: MediaDeviceInfo[] = [];
  videoDevices: MediaDeviceInfo[] = [];
  selectedAudioDevice: string = '';
  selectedVideoDevice: string = '';
  organizationName: string = '';
  logoUrl: string | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private interviewService: AIScreeningService,
    private aiSessionService: AiInterviewSessionService,
    public connectivity: ConnectivityService) {
    // this.startCamera();
    this.interviewForm = this.fb.group({
      role_title: ['', Validators.required],
      duration_minutes: [0, Validators.required],
      candidate_id: [0, Validators.required],
      front_end_session_id: [0, Validators.required],
      interviewer_id: [1],
      job_description: [''],
      skills: this.fb.array([]),
      questions: [''],
      microphone_status: [false],
      camera_status: [false]
    });
  }

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('is_b2c_practice') === 'true' || sessionStorage.getItem('is_b2c_practice') === 'true') {
      this.isB2cPractice = true;
    }
    const candidateIdParam = this.route.snapshot.paramMap.get('candidateId');
    const sessionIdParam = this.route.snapshot.paramMap.get('sessionId');
    const screeningIdParam = this.route.snapshot.paramMap.get('screeningId');

    const token = sessionStorage.getItem('screening_access_code');

    this.candidateId = candidateIdParam ? (isNaN(Number(candidateIdParam)) ? candidateIdParam : Number(candidateIdParam)) : null as any;
    this.sessionId = sessionIdParam ? (isNaN(Number(sessionIdParam)) ? sessionIdParam : Number(sessionIdParam)) : null as any;
    this.screeningId = screeningIdParam || undefined;

    if (!this.candidateId || !this.sessionId || !this.screeningId || !token) {
      this.toastr.warning(
        'Session information is missing. Please verify your OTP again.',
        'Verification Required'
      );
      return;
    }
    this.screeningAccessCode = token;
    this.organizationName = sessionStorage.getItem('organization_name') || '';
    this.logoUrl = sessionStorage.getItem('logo_url');

    const langParam = this.route.snapshot.queryParamMap.get('lang');
    if (langParam) {
      sessionStorage.setItem('preferred_interview_language', langParam);
    }

    if (this.screeningId) {
      this.getRoundDetails(this.screeningId);
    }

    this.interviewForm.patchValue({
      candidate_id: this.candidateId,
      front_end_session_id: this.sessionId
    });

    this.connectivity.quality$.pipe(takeUntil(this.destroy$)).subscribe(q => {
      this.networkQuality = q;
      this.cdr.detectChanges();
    });
    this.connectivity.checkQuality();

    this.setupPermissionListeners();
    this.getDevices();
  }

  async setupPermissionListeners() {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        this.cameraPermissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        this.micPermissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

        this.cameraPermissionStatus.onchange = () => {
          this.handlePermissionStateChange();
        };

        this.micPermissionStatus.onchange = () => {
          this.handlePermissionStateChange();
        };
      }
    } catch (error) {
      console.log('Permissions API not fully supported', error);
    }
  }

  handlePermissionStateChange() {
    const camState = this.cameraPermissionStatus?.state;
    const micState = this.micPermissionStatus?.state;

    if (camState === 'denied' || micState === 'denied') {
      this.isPermissionsRevoked = true;
      this.permissionsGranted = false;
      this.cameraActive = false;
      this.micActive = false;
      this.cameraPermissionDenied = true;
      this.micPermissionDenied = true;
      this.permissionErrorMessage = 'Camera & microphone access has been revoked. Please enable it from your browser site settings.';
      this.stopMediaTracks();
      this.cdr.detectChanges();
    } else if (camState === 'granted' && micState === 'granted') {
      this.isPermissionsRevoked = false;
      this.permissionErrorMessage = '';
      if (!this.permissionsGranted && !this.isRequestingPermissions) {
        this.requestPermissions();
      }
    } else {
      this.isPermissionsRevoked = false;
      if (this.permissionsGranted) {
        this.permissionsGranted = false;
        this.cameraActive = false;
        this.micActive = false;
        this.stopMediaTracks();
        this.cdr.detectChanges();
      }
    }
  }

  handleBack() {
    this.router.navigate(['/verification']);
  }

  getDevices() {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      this.audioDevices = devices.filter(d => d.kind === 'audioinput');
      this.videoDevices = devices.filter(d => d.kind === 'videoinput');
      
      const hasLabels = devices.some(d => d.label !== '');

      if (this.audioDevices.length > 0 && !this.selectedAudioDevice) {
        this.selectedAudioDevice = this.audioDevices[0].deviceId;
      }
      if (this.videoDevices.length > 0 && !this.selectedVideoDevice) {
        this.selectedVideoDevice = this.videoDevices[0].deviceId;
      }

      this.cdr.detectChanges();
    }).catch(err => {
      console.error('Error fetching devices', err);
    });
  }

  changeAudioDevice(event: any) {
    this.selectedAudioDevice = event.target.value;
    if (this.permissionsGranted) {
      this.requestPermissions(true);
    }
  }

  changeVideoDevice(event: any) {
    this.selectedVideoDevice = event.target.value;
    if (this.permissionsGranted) {
      this.requestPermissions(true);
    }
  }

  // handleBack() {
  //   this.router.navigate(['/verification']);
  // }

  async requestPermissions(forceRestart: boolean = false) {
    if (this.isRequestingPermissions || (this.permissionsGranted && !forceRestart)) return;
    
    if (this.isPermissionsRevoked) {
      this.permissionErrorMessage = 'Camera & microphone access is currently blocked. Please enable it from your browser site settings.';
      this.cdr.detectChanges();
      return;
    }
    
    this.isRequestingPermissions = true;
    this.permissionErrorMessage = '';
    this.cameraPermissionDenied = false;
    this.micPermissionDenied = false;
    this.cdr.detectChanges();

    this.stopMediaTracks();

    try {
      const constraints: MediaStreamConstraints = {
        video: this.selectedVideoDevice ? { deviceId: { exact: this.selectedVideoDevice } } : true,
        audio: this.selectedAudioDevice ? {
          deviceId: { exact: this.selectedAudioDevice },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } : {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      this.permissionsGranted = true;
      this.cameraActive = true;
      this.micActive = true;

      this.cameraStream = new MediaStream(stream.getVideoTracks());
      this.micStream = new MediaStream(stream.getAudioTracks());

      // Add onmute listener to detect hardware/OS level muting
      const audioTrack = this.micStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onmute = () => {
          this.ngZone.run(() => {
            this.hasAudioBeenDetected = false;
            this.lastAudioDetectedTime = 0;
            this.cdr.detectChanges();
          });
        };
      }

      if (this.videoElement && this.videoElement.nativeElement) {
        const video = this.videoElement.nativeElement;
        video.srcObject = this.cameraStream;
        video.onloadedmetadata = () => {
          video.play().catch(e => console.error('Video play error', e));
        };
      }

      this.audioContext = new AudioContext();
      const resumePromise = (this.audioContext.state === 'suspended') ? this.audioContext.resume() : Promise.resolve();
      
      resumePromise.then(() => {
        this.analyser = this.audioContext!.createAnalyser();
        this.analyser.fftSize = 256;
        this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
        
        const source = this.audioContext!.createMediaStreamSource(this.micStream!);
        source.connect(this.analyser!);
        
        this.ngZone.runOutsideAngular(() => this.updateVolumeLoop());
      });

      if (!forceRestart) {
        this.getDevices();
      }

    } catch (error: any) {
      this.permissionsGranted = false;
      this.handlePermissionError(error);
    } finally {
      this.isRequestingPermissions = false;
      this.cdr.detectChanges();
    }
  }

  handlePermissionError(error: any) {
    console.error('Media permission error:', error);
    this.cameraActive = false;
    this.micActive = false;
    
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      this.permissionErrorMessage = 'Camera & microphone access has been revoked. Please enable it from your browser site settings.';
      this.cameraPermissionDenied = true;
      this.micPermissionDenied = true;
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      this.permissionErrorMessage = 'No camera or microphone found. Please connect a device and try again.';
      this.cameraPermissionDenied = true;
      this.micPermissionDenied = true;
    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      this.permissionErrorMessage = 'Your camera or microphone is already in use by another application.';
      this.cameraPermissionDenied = true;
      this.micPermissionDenied = true;
    } else {
      this.permissionErrorMessage = 'An error occurred while accessing media devices. Please ensure your browser supports media devices.';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopMediaTracks();
  }

  stopMediaTracks() {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }
    if (this.micStream) {
      this.micStream.getTracks().forEach(track => track.stop());
      this.micStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.analyser = null;
    this.cameraActive = false;
    this.micActive = false;
    this.hasAudioBeenDetected = false;
    this.lastAudioDetectedTime = 0;
    this.volume = 0;
    this.cdr.detectChanges();
  }

  refreshAll() {
    this.stopMediaTracks();
    this.requestPermissions(true);
    this.connectivity.checkQuality();
  }

  private updateVolumeLoop() {
    if (!this.analyser || !this.dataArray) return;

    this.analyser.getByteFrequencyData(this.dataArray);

    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) sum += this.dataArray[i];
    const average = sum / this.dataArray.length;
    const newVolume = Math.min(100, Math.round((average / 255) * 100));

    this.ngZone.run(() => {
      this.volume = newVolume;
      const now = Date.now();
      
      if (newVolume > 0) {
        this.lastAudioDetectedTime = now;
        if (!this.hasAudioBeenDetected) {
          this.hasAudioBeenDetected = true;
        }
      } else {
        // If volume is 0 for more than 5 seconds, reset detection
        if (this.hasAudioBeenDetected && (now - this.lastAudioDetectedTime > 5000)) {
          this.hasAudioBeenDetected = false;
        }
      }
      
      this.cdr.detectChanges();
    });

    this.animationId = requestAnimationFrame(() => this.updateVolumeLoop());
  }

  getRoundDetails(id: string) {
    const accessCode = sessionStorage.getItem('screening_access_code');
    const request$ = accessCode
      ? this.interviewService.getPublicScreeningRoundDetails(accessCode, id)
      : this.interviewService.getScreeningRoundDetails(id);

    request$.subscribe({
      next: (response) => {
        const isSuccess = response?.success ?? (response as any)?.Success;
        if (isSuccess) {
          this.roundDetails.set(response?.data ?? (response as any)?.Data);
        }
        else {
          this.toastr.error(response?.message || (response as any)?.Message || 'Failed to fetch screening round details', 'Error');
        }
      },
      error: (error) => {
        this.toastr.error('Failed to fetch screening round details', 'Error');
      }
    });
  }

  async startInterview() {
    if (this.isLoading) return;
    if (!this.permissionsGranted || !this.cameraActive || !this.micActive) return;
    
    this.isLoading = true;

    const round = this.roundDetails();
    if (round) {
      this.interviewForm.patchValue({
        role_title: round.round.job_role,
        duration_minutes: round.round.total_duration_minutes,
        interviewer_id: 1,
        job_description: round.round.job_description,
        camera_status: this.cameraActive,
        microphone_status: this.micActive
      });

      const skillsArray = this.interviewForm.get('skills') as FormArray;
      skillsArray.clear();

      round.round.skills.forEach(skill => {
        skillsArray.push(this.fb.control(skill));
      });
    }

    // Read candidate personal info from sessionStorage
    const candidateInfoRaw = sessionStorage.getItem('candidate_info');
    const candidateInfo = candidateInfoRaw ? JSON.parse(candidateInfoRaw) : {};

    // Read resume text from sessionStorage
    const resumeText = sessionStorage.getItem('resume_text');
    const resume_url = localStorage.getItem('resume_url');

    let questionsArr: string[] = [];
    let rawQuestions: any = this.roundDetails()?.round.questions;

    // The backend might send the string double-stringified, or as a nested array string.
    // We recursively parse it as long as it looks like a stringified JSON array.
    if (rawQuestions) {
      while (typeof rawQuestions === 'string' && rawQuestions.trim().startsWith('[')) {
        try {
          rawQuestions = JSON.parse(rawQuestions);
        } catch (e) {
          break;
        }
      }

      if (Array.isArray(rawQuestions)) {
        questionsArr = rawQuestions.flat(Infinity);
      } else {
        // Newline-separated fallback (legacy comma-separated also handled gracefully)
        questionsArr = String(rawQuestions).split('\n').map(q => q.trim()).filter(q => q.length > 0);
      }
    }

    const payload = {
      token: this.screeningAccessCode,
      source_app: "BEEXPERT",
      role_title: this.interviewForm.value.role_title,
      duration_minutes: this.interviewForm.value.duration_minutes,
      interviewer_id: this.interviewForm.value.interviewer_id,
      job_description: this.interviewForm.value.job_description,
      camera_status: this.interviewForm.value.camera_status,
      microphone_status: this.interviewForm.value.microphone_status,
      skills: JSON.stringify(this.interviewForm.value.skills || []),
      candidate_id: this.interviewForm.value.candidate_id,
      front_end_session_id: this.interviewForm.value.front_end_session_id,
      passing_score: this.roundDetails()?.round.passing_score,
      questions: JSON.stringify(questionsArr),
      candidate_name: candidateInfo.name || '',
      candidate_email: candidateInfo.email || '',
      candidate_phone: candidateInfo.phone || '',
      is_teacher: this.roundDetails()?.round.is_teacher,
      resume_text: this.isB2cPractice ? '' : (resumeText || ''),
      resume_url: this.isB2cPractice ? '' : (resume_url || ''),
      preferred_language: this.route.snapshot.queryParamMap.get('lang') || sessionStorage.getItem('preferred_interview_language') || 'en',
      is_b2c_practice: this.isB2cPractice
    };

    const body = this.toFormUrlEncoded(payload);
    this.interviewService.startInterview(body).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.cdr.detectChanges();
        if (res.Success && res.Data) {
          this.aiSessionService.update({
            session_id: res.Data.session_id,
            ephemeral_token: res.Data.ephemeral_token,
            webrtc_config: res.Data.webrtc_config,
            openai_api_url: res.Data.openai_api_url,
            model: res.Data.model,
            duration_minutes: res.Data.interview_context.duration,
            job_title: this.roundDetails()?.round.job_role,
            interviewer_name: res.Data.interviewer.name,
            // Stored so interview-panel can inject educator-specific guidance
            // and tailor the voice-analysis end-of-session request.
            is_teacher: this.roundDetails()?.round.is_teacher ?? false,
            is_b2c_practice: this.isB2cPractice,
            // Resolved interview language config (greeting, display name) from the backend
            language: res.Data.language,
            supported_languages: res.Data.supported_languages,
            runtime_instructions: res.Data.runtime_instructions
          });

          sessionStorage.setItem('is_b2c_practice', this.isB2cPractice ? 'true' : 'false');
          if (this.sessionId) {
            sessionStorage.setItem(`is_b2c_practice_${this.sessionId}`, this.isB2cPractice ? 'true' : 'false');
          }
          if (res.Data.session_id) {
            sessionStorage.setItem(`is_b2c_practice_${res.Data.session_id}`, this.isB2cPractice ? 'true' : 'false');
          }

          // Pass mandatory questions to interview-panel so it can inject
          // a strategic instruction into the AI session after WebRTC connects.
          if (questionsArr.length > 0) {
            sessionStorage.setItem('mandatory_questions', JSON.stringify(questionsArr));
          } else {
            sessionStorage.removeItem('mandatory_questions');
          }
          this.router.navigate(['/interview-panel', this.sessionId], {
            queryParams: this.isB2cPractice ? { is_b2c_practice: 'true' } : {}
          });
        } else {
          this.toastr.warning(res.Message || 'Failed to create interview session.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.cdr.detectChanges();
        this.toastr.error('Error creating session.');
      },
    });
  }

  private toFormUrlEncoded(data: any): string {
    const params = new URLSearchParams();

    Object.keys(data).forEach(key => {
      const value = data[key];

      if (Array.isArray(value)) {
        // handle array values (skills)
        value.forEach((item: any) => {
          params.append(`${key}[]`, item);
        });
      } else if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    return params.toString();
  }
}
